<template>
  <div class="wrapper">
    <div class="header_row">
      <div class="title_group">
        <h2 class="title">海外就業安全：三分鐘自我檢核</h2>
        <p>共12題，針對尋職期、登機前、抵達後提供安全建議。</p>
      </div>
      <a
        class="download_btn"
        href="/files/overseas-employment-self-check.pdf"
        download="海外就業一二三 求職防騙好心安(海外就業自我檢核表含QR CODE).pdf"
      >
        點我下載完整問卷
        <DownloadOutlined />
      </a>
    </div>

    <button
      v-if="!started"
      class="diagnose_btn"
      type="button"
      @click="startQuiz"
    >
      開始診斷 <img class="arrow" src="../img/arrow.png" alt="" />
    </button>

    <div v-if="started" class="quiz">
      <div class="quiz_progress">
        <div class="quiz_progress_head">
          <p>問卷進度，點擊可跳至該題</p>
          <p class="quiz_progress_count">
            {{ answeredCount }} / {{ questions.length }}
          </p>
        </div>
        <div class="quiz_progress_bar" :class="{ readonly: finished }">
          <span
            v-for="(q, index) in questions"
            :key="q.id"
            class="quiz_progress_seg"
            :class="segmentClass(index)"
            role="button"
            :tabindex="finished ? -1 : 0"
            :aria-label="segmentLabel(index)"
            @click="goToQuestion(index)"
            @keydown.enter.prevent="goToQuestion(index)"
            @keydown.space.prevent="goToQuestion(index)"
          ></span>
        </div>
        <p class="quiz_progress_phase">
          1.~9. 準備出國前&ensp;|&ensp;10.~12. 抵達海外後
        </p>
      </div>

      <template v-if="!finished">
        <div class="quiz_card">
          <div class="quiz_question_head">
            <span class="quiz_number">{{ currentIndex + 1 }}</span>
            <div class="quiz_question_text">
              <p class="quiz_question">{{ currentQuestion.question }}</p>
              <p v-if="currentQuestion.description" class="quiz_desc">
                {{ currentQuestion.description }}
              </p>
            </div>
          </div>
          <div style="padding: 32px">
            <div class="quiz_answers">
              <button
                type="button"
                class="quiz_answer_btn"
                :class="{ selected: answers[currentIndex] === 'yes' }"
                :aria-pressed="answers[currentIndex] === 'yes'"
                @click="setAnswer('yes')"
              >
                <span class="answer_icon answer_icon_yes"></span>
                是 (Yes)
              </button>
              <button
                type="button"
                class="quiz_answer_btn"
                :class="{ no_selected: answers[currentIndex] === 'no' }"
                :aria-pressed="answers[currentIndex] === 'no'"
                @click="setAnswer('no')"
              >
                <span class="answer_icon answer_icon_no"></span>
                否 (No)
              </button>
            </div>
          </div>
          <div style="padding: 0px 32px 32px 32px">
            <div
              v-if="hasQuestionWarning(currentQuestion.warning)"
              class="quiz_warning"
            >
              <div class="quiz_warning_body">
                <img
                  v-if="currentQuestion.warning.text"
                  class="quiz_warning_icon"
                  src="../img/warning.png"
                  alt=""
                />
                <div class="quiz_warning_text">
                  <p>{{ currentQuestion.warning.text }}</p>
                  <a
                    v-if="currentQuestion.warning.linkUrl"
                    :href="currentQuestion.warning.linkUrl"
                    :title="'[另開新視窗]' + currentQuestion.warning.linkText"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExportOutlined v-if="currentQuestion.warning.linkText" />
                    {{ currentQuestion.warning.linkText }}
                  </a>
                </div>
              </div>
              <div
                v-if="currentQuestion.warning.linkUrl"
                class="quiz_warning_qr"
              >
                <img :src="qrCodeDataUrl" alt="" />
              </div>
            </div>
          </div>
        </div>

        <div class="quiz_nav">
          <button type="button" class="quiz_back_btn" @click="goBack">
            <LeftOutlined /> 返回
          </button>
          <button
            v-if="!isLastQuestion"
            type="button"
            class="quiz_next_btn"
            @click="goNext"
          >
            下一題 <RightOutlined />
          </button>
          <button
            v-else
            type="button"
            class="quiz_next_btn"
            :disabled="!allAnswered"
            @click="finishQuiz"
          >
            完成 <RightOutlined />
          </button>
        </div>
      </template>

      <div v-else class="quiz_result">
        <div class="result_box">
          <div class="risk_reference">
            <div class="risk_reference_title">
              <BarChartOutlined />
              <h3>風險等級參考</h3>
            </div>
            <div class="risk_reference_list">
              <div
                v-for="level in riskLevels"
                :key="level.key"
                class="risk_reference_item"
                :class="{ active: level.key === currentRiskLevel.key }"
              >
                <p class="risk_name">
                  <span
                    class="risk_dot"
                    :style="{
                      backgroundColor: level.color,
                      ...activeLevelStyle(level),
                    }"
                  ></span>
                  {{ level.label }}
                </p>
                <p class="risk_desc">{{ level.range }}。{{ level.legend }}</p>
              </div>
            </div>
          </div>

          <div class="result_summary">
            <span
              class="result_icon_bg"
              :style="{ backgroundColor: currentRiskLevel.color + '1A' }"
            >
              <span
                class="result_icon"
                :style="{ backgroundColor: currentRiskLevel.color }"
              >
                <component :is="currentRiskLevel.icon" />
              </span>
            </span>
            <div class="result_text_group">
              <h3 :style="{ color: currentRiskLevel.color }">
                {{ currentRiskLevel.resultLabel }}
              </h3>
              <p class="result_subtitle">海外就業風險診斷結果</p>
            </div>
          </div>

          <p class="result_line">
            您在{{ questions.length }}題中有{{
              yesCount
            }}題選擇『是』，診斷結果為{{ currentRiskLevel.resultLabel }}。
          </p>
          <p class="result_advice">{{ currentRiskLevel.advice }}</p>

          <template v-if="attentionQuestions.length">
            <p class="attention_title">您要注意的有：</p>
            <div
              v-for="q in attentionQuestions"
              :key="q.id"
              class="quiz_card attention_card"
              :class="{ corrected: isAnswerCorrected(q.index) }"
            >
              <div class="quiz_question_head">
                <span class="quiz_number">{{ q.index + 1 }}</span>
                <div class="quiz_question_text">
                  <p class="quiz_question">{{ q.question }}</p>
                  <p v-if="q.description" class="quiz_desc">
                    {{ q.description }}
                  </p>
                </div>
                <span v-if="isAnswerCorrected(q.index)" class="corrected_badge">
                  <CheckOutlined /> 已修正
                </span>
              </div>
              <div style="padding: 32px">
                <div class="quiz_answers">
                  <button
                    type="button"
                    class="quiz_answer_btn"
                    :class="{ selected: answers[q.index] === 'yes' }"
                    :aria-pressed="answers[q.index] === 'yes'"
                    @click="correctAnswer(q.index, 'yes')"
                  >
                    <span class="answer_icon answer_icon_yes"></span>
                    是 (Yes)
                  </button>
                  <button
                    type="button"
                    class="quiz_answer_btn"
                    :class="{ no_selected: answers[q.index] === 'no' }"
                    :aria-pressed="answers[q.index] === 'no'"
                    @click="correctAnswer(q.index, 'no')"
                  >
                    <span class="answer_icon answer_icon_no"></span>
                    否 (No)
                  </button>
                </div>
              </div>
              <div
                v-if="hasQuestionWarning(q.warning)"
                style="padding: 0px 32px 32px 32px"
              >
                <div class="quiz_warning">
                  <div class="quiz_warning_body">
                    <img
                      v-if="q.warning.text"
                      class="quiz_warning_icon"
                      src="../img/warning.png"
                      alt=""
                    />
                    <div class="quiz_warning_text">
                      <p>{{ q.warning.text }}</p>
                      <a
                        v-if="q.warning.linkUrl"
                        :href="q.warning.linkUrl"
                        :title="'[另開新視窗]' + q.warning.linkText"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExportOutlined v-if="q.warning.linkText" />
                        {{ q.warning.linkText }}
                      </a>
                    </div>
                  </div>
                  <div v-if="q.warning.linkUrl" class="quiz_warning_qr">
                    <img :src="qrCodeMap[q.id]" alt="" />
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <div class="quiz_nav">
          <button
            v-if="hasIncorrectAnswers || !resultConfirmed"
            type="button"
            class="quiz_next_btn"
            :disabled="hasIncorrectAnswers"
            @click="confirmResult"
          >
            完成 <EditOutlined />
          </button>
          <button
            v-else
            type="button"
            class="quiz_next_btn restart_btn"
            @click="restartQuiz"
          >
            重新測驗 <EditOutlined />
          </button>
        </div>

        <div class="other_reminder">
          <div class="other_reminder_head">
            <InfoCircleOutlined />
            <p>其他提醒</p>
          </div>
          <div class="other_reminder_body">
            <p class="other_reminder_text">
              青年學生亦可參考教育部的
              <span class="highlight">國外打工度假行前自我檢核表</span>
              進行檢核。
            </p>
            <a
              class="other_reminder_download"
              href="/files/youth-pre-departure-self-check.pdf"
              download="青年學生赴國外度假打工行前自我檢核表.pdf"
            >
              下載 <DownloadOutlined />
            </a>
          </div>
        </div>
      </div>
    </div>

    <a-modal
      v-model:visible="showConfirmModal"
      :footer="null"
      :closable="false"
      width="400px"
      class="confirm_modal"
    >
      <div class="confirm_modal_body">
        <div class="confirm_icon">
          <img src="../img/warning_check_icon.png" alt="" />
        </div>
        <h3>提醒：此項目未確認</h3>
        <p>您在此項目尚未完成確認。即使前往下一題，您之後仍可再次檢查。</p>
        <button
          type="button"
          class="confirm_recheck_btn"
          @click="recheckQuestion"
        >
          重新檢查 <ReloadOutlined />
        </button>
        <button type="button" class="confirm_skip_btn" @click="skipToNext">
          前往下一題 <ArrowRightOutlined />
        </button>
      </div>
    </a-modal>

    <div v-if="!finished" class="hotline_card">
      <div class="hotline_item">
        <p class="label">緊急服務專線</p>
        <p class="number">0800-085-095</p>
      </div>
      <div class="hotline_item">
        <p class="label">急難救助全球免付費專線</p>
        <p class="number">880-0885-0885</p>
      </div>
      <a
        class="usage_link"
        title="[另開新視窗]急難救助全球免付費專線撥打使用說明"
        href="https://www.boca.gov.tw/cp-87-2121-7a5da-1.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        撥打使用說明
        <InfoCircleFilled />
      </a>
    </div>

    <div v-if="!finished" class="gov_resource">
      <div class="gov_resource_title">
        <BankOutlined />
        <p>政府官方資源</p>
      </div>
      <p class="gov_resource_desc">
        如有任何疑慮或需緊急救助，請聯繫以下官方管道
      </p>
      <div class="gov_resource_list">
        <a
          class="gov_resource_item"
          href="https://www.mofa.gov.tw/Theme.aspx?n=3915&sms=69&s=136"
          title="[另開新視窗]外交部「海外求職陷阱宣導專區」"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span class="icon_circle">
            <img src="../img/logo_MOFA.png" alt="中華民國外交部" />
          </span>
          <span class="text_group">
            <p class="org_name">中華民國外交部</p>
            <p class="desc">海外求職陷阱宣導專區</p>
          </span>
        </a>
        <a
          class="gov_resource_item"
          href="https://youthtaiwan.mofa.gov.tw/WorkingHoliday/"
          title="[另開新視窗]外交部「青年度假打工網站專區」"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span class="icon_circle">
            <img src="../img/logo_MOFA.png" alt="中華民國外交部" />
          </span>
          <span class="text_group">
            <p class="org_name">中華民國外交部</p>
            <p class="desc">青年度假打工網站專區</p>
          </span>
        </a>
        <a
          class="gov_resource_item"
          href="https://165.npa.gov.tw/#/"
          title="[另開新視窗]內政部警政署「165全民防騙網」"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span class="icon_circle">
            <img src="../img/logo_NPA.png" alt="內政部警政署" />
          </span>
          <span class="text_group">
            <p class="org_name">內政部警政署</p>
            <p class="desc">165反詐騙專區</p>
          </span>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, reactive, ref, watch } from "vue";
import { useStore } from "vuex";
import QRCode from "qrcode";
import {
  DownloadOutlined,
  InfoCircleFilled,
  InfoCircleOutlined,
  BankOutlined,
  GlobalOutlined,
  ExportOutlined,
  LeftOutlined,
  RightOutlined,
  EditOutlined,
  BarChartOutlined,
  CheckOutlined,
  ReloadOutlined,
  ArrowRightOutlined,
  ExclamationOutlined,
  WarningFilled,
} from "@ant-design/icons-vue";
import { apiGuide } from "@/api/api.js";

const questions = [
  {
    id: 1,
    question: "是否有確認過求職管道的安全性？",
    description:
      "如果透過國內仲介公司媒合職缺，是否有先查詢確認為合法私立就業服務機構？",
    warning: {
      text: "★對於社群軟體或社交網站的對話及廣告資訊要提高警覺。",
      linkText: "前往勞動部「私立就業服務機構查詢系統」",
      linkUrl: "http://agent.wda.gov.tw/agentext/agent/QryAgent.jsp ",
    },
  },
  {
    id: 2,
    question: "是否有查過海外雇主的背景資料，確認在海外是合法登記？",
    description: "",
    warning: {
      text: "★要留意博弈、投資放款、娛樂性產業，風險較高。",
      linkText: "前往外交部「海外求職陷阱宣導專區」",
      linkUrl: "https://www.mofa.gov.tw/Theme.aspx?n=3915&sms=69&s=136",
    },
  },
  {
    id: 3,
    question:
      "是否有確認工作職缺內容，避免薪資待遇不合理誇大及高風險地區職缺？",
    description: "",
    warning: {
      text: "高風險地區為柬埔寨、緬甸、寮國、泰國及菲律賓等東南亞國家，以及阿拉伯聯合大公國(杜拜)、烏干達、肯亞、土耳其等地。",
      linkText: "前往外交部「海外求職詐騙實際案例」",
      linkUrl: "https://www.mofa.gov.tw/cp.aspx?n=4086",
    },
  },
  {
    id: 4,
    question: "是否告知家人及親友應徵面試細節及擬赴國家與雇主資訊？",
    description: "",
    warning: {
      text: "★如果認為有詐騙疑慮，記得向相關單位尋求協助。 ",
      linkText: "前往內政部警政署「165全民防騙網」",
      linkUrl: "https://165.npa.gov.tw/#/ ",
    },
  },
  {
    id: 5,
    question:
      "是否有蒐集海外工作當地國資訊，包含勞動法規、居住條件、金融及交通租屋等資訊，並已衡量評估符合自身經濟狀況及語言能力？",
    description: "",
    warning: {
      text: "",
      linkText: "前往外交部「中華民國駐外單位網站聯結」",
      linkUrl: "http://www.taiwanembassy.org",
    },
  },
  {
    id: 6,
    question: "是否已先與雇主簽訂書面契約以保障相關權益?",
    description: "",
    warning: {
      text: "",
      linkText: "前往勞動部勞動力發展署「國人海外就業資源中心」",
      linkUrl: "https://overseas.taiwanjobs.gov.tw/ ",
    },
  },
  {
    id: 7,
    question: "是否有辦理出國簽證並備妥護照？並購買相關足額保險？ ",
    description: "",
    warning: {
      text: "",
      linkText: "",
      linkUrl: "",
    },
  },
  {
    id: 8,
    question:
      "是否有在外交部領事事務局「旅外安全-出國登錄」專區登錄海外期間緊急聯絡相關資訊？ ",
    description: "",
    warning: {
      text: "",
      linkText: "前往外交部領事事務局「旅外安全-出國登錄」",
      linkUrl: "https://www.boca.gov.tw/sp-abre-main-1.html",
    },
  },
  {
    id: 9,
    question:
      "是否牢記「外交部緊急聯絡中心」設置的「旅外國人緊急服務專線電話」為0800-085-095【諧音「您幫我、您救我」】？",
    description: "",
    warning: {
      text: "★海外付費則需先撥(當地國國際電話冠碼) +886-800-085-095",
      linkText: "前往外交部領事事務局「旅外國人急難救助全球免付費專線」",
      linkUrl: "https://www.boca.gov.tw/cp-56-2523-8636d-1.html",
    },
  },
  {
    id: 10,
    question: "是否持續與家人及親友保持聯繫？",
    description: "",
    warning: {
      text: "★如果於海外期間對於人身安全有疑慮時，應即刻反映及尋求協助。",
      linkText: "",
      linkUrl: "",
    },
  },
  {
    id: 11,
    question:
      "是否瞭解護照、簽證及手機宜親自保管不離身，並可事先準備影本備用？",
    description: "",
    warning: {
      text: "★倘護照或簽證遭竊或遺失，應向當地警察單位報案，並持報案證明，向我國駐外館處申請補發護照或入國證明書。",
      linkText: "",
      linkUrl: "",
    },
  },
  {
    id: 12,
    question:
      "是否暸解信用卡或提款卡遺失或遭竊時，應立即向所屬銀行或發卡公司申報作廢、止付，並向當地警察單位報案，以防被盜領？",
    description: "",
    warning: {
      text: "",
      linkText: "",
      linkUrl: "",
    },
  },
];

const riskLevels = [
  {
    key: "safe",
    label: "安全 (Safe)",
    resultLabel: "安全",
    color: "#0288D1",
    icon: CheckOutlined,
    min: 10,
    max: 12,
    range: "10-12題「是」",
    legend: "環境極其穩定，適合前往，僅需具備基本警覺心。",
    advice:
      "海外就業環境穩定，風險低可安心前往。我們建議您繼續保持警覺，並定期查看最新的政府海外就業安全公告。",
  },
  {
    key: "low",
    label: "低風險 (Low)",
    resultLabel: "低風險",
    color: "#2E7D32",
    icon: CheckOutlined,
    min: 7,
    max: 9,
    range: "7-9題「是」",
    legend: "整體環境良好，偶有小規模波動，需多留意當地新聞。",
    advice:
      "海外就業環境大致穩定，但仍有部分風險因素存在。建議您出發前多加留意當地治安與新聞動態，並隨時與家人親友保持聯繫。",
  },
  {
    key: "medium",
    label: "中風險 (Medium)",
    resultLabel: "中風險",
    color: "#F9A825",
    icon: ExclamationOutlined,
    min: 4,
    max: 6,
    range: "4-6題「是」",
    legend: "存在一定就業風險，建議諮詢專業機構或尋求政府協助。",
    advice:
      "海外就業環境存在一定風險，建議您在出發前審慎評估，並可諮詢外交部或勞動部等專業機構，取得更完整的當地資訊與協助。",
  },
  {
    key: "high",
    label: "高風險 (High)",
    resultLabel: "高風險",
    color: "#BA1A1A",
    icon: WarningFilled,
    min: 0,
    max: 3,
    range: "0-3題「是」",
    legend: "高度不穩定環境，強烈建議取消行程或延期，避免前往。",
    advice:
      "海外就業環境高度不穩定，強烈建議您重新考慮此行程或暫緩前往。如仍須前往，請務必尋求政府相關單位協助，並做好完整風險準備。",
  },
];

export default defineComponent({
  name: "FraudSelfCheck",
  components: {
    DownloadOutlined,
    InfoCircleFilled,
    InfoCircleOutlined,
    BankOutlined,
    GlobalOutlined,
    ExportOutlined,
    LeftOutlined,
    RightOutlined,
    EditOutlined,
    BarChartOutlined,
    CheckOutlined,
    ReloadOutlined,
    ArrowRightOutlined,
  },
  setup() {
    const guides = reactive([]);
    apiGuide()
      .then((res) => {
        res.data.forEach((i) => {
          guides.push(i);
        });
      })
      .catch((err) => {
        console.log(err);
      });

    const foreignAffairsGuide = computed(() =>
      guides.find((guide) => guide.pageTitle === "外交國家資訊"),
    );
    const safetyAbroadGuide = computed(() =>
      guides.find((guide) => guide.pageTitle === "安全旅外資訊"),
    );

    const started = ref(false);
    const finished = ref(false);
    const currentIndex = ref(0);
    const answers = reactive(Array(questions.length).fill(null));

    const currentQuestion = computed(() => questions[currentIndex.value]);
    const hasQuestionWarning = (warning) =>
      Boolean(warning && (warning.text || warning.linkText || warning.linkUrl));
    const isLastQuestion = computed(
      () => currentIndex.value === questions.length - 1,
    );
    const answeredCount = computed(
      () => answers.filter((answer) => answer !== null).length,
    );
    const allAnswered = computed(() =>
      answers.every((answer) => answer !== null),
    );

    const yesCount = computed(
      () => answers.filter((answer) => answer === "yes").length,
    );
    const currentRiskLevel = computed(
      () =>
        riskLevels.find(
          (level) => yesCount.value >= level.min && yesCount.value <= level.max,
        ) || riskLevels[riskLevels.length - 1],
    );
    const activeLevelStyle = (level) =>
      level.key === currentRiskLevel.value.key
        ? { boxShadow: `0px 0px 8px 0px ${level.color}80` }
        : {};
    const hasIncorrectAnswers = computed(() =>
      answers.some((answer) => answer === "no"),
    );
    const visibleAttentionIndices = reactive(new Set());
    const syncVisibleAttentionIndices = () => {
      questions.forEach((q, index) => {
        if (answers[index] === "no") visibleAttentionIndices.add(index);
      });
    };
    const attentionQuestions = computed(() =>
      questions
        .map((q, index) => ({ ...q, index }))
        .filter((q) => visibleAttentionIndices.has(q.index)),
    );
    const isAnswerCorrected = (index) => answers[index] === "yes";
    const resultConfirmed = ref(false);

    // 該題有 warning.linkUrl 才產生 QR code
    const qrCodeDataUrl = ref("");
    watch(
      () => currentQuestion.value.warning?.linkUrl,
      (linkUrl) => {
        if (!linkUrl) {
          qrCodeDataUrl.value = "";
          return;
        }
        QRCode.toDataURL(linkUrl.trim())
          .then((dataUrl) => {
            qrCodeDataUrl.value = dataUrl;
          })
          .catch((err) => {
            console.log(err);
          });
      },
      { immediate: true },
    );

    const qrCodeMap = reactive({});
    watch(finished, (isFinished) => {
      if (!isFinished) return;
      attentionQuestions.value.forEach((q) => {
        const linkUrl = q.warning?.linkUrl?.trim();
        if (!linkUrl || qrCodeMap[q.id]) return;
        QRCode.toDataURL(linkUrl)
          .then((dataUrl) => {
            qrCodeMap[q.id] = dataUrl;
          })
          .catch((err) => {
            console.log(err);
          });
      });
    });

    const startQuiz = () => {
      started.value = true;
      finished.value = false;
      currentIndex.value = 0;
      resultConfirmed.value = false;
      visibleAttentionIndices.clear();
    };
    const setAnswer = (value) => {
      answers[currentIndex.value] = value;
    };
    const correctAnswer = (index, value) => {
      answers[index] = value;
      resultConfirmed.value = false;
      if (value === "no") visibleAttentionIndices.add(index);
    };
    const confirmResult = () => {
      if (hasIncorrectAnswers.value) return;
      resultConfirmed.value = true;
      visibleAttentionIndices.clear();
    };
    const goToQuestion = (index) => {
      if (finished.value) return;
      currentIndex.value = index;
    };
    const showConfirmModal = ref(false);
    const goNext = () => {
      if (isLastQuestion.value) return;
      if (answers[currentIndex.value] === null) {
        showConfirmModal.value = true;
        return;
      }
      currentIndex.value += 1;
    };
    const recheckQuestion = () => {
      showConfirmModal.value = false;
    };
    const skipToNext = () => {
      showConfirmModal.value = false;
      if (!isLastQuestion.value) currentIndex.value += 1;
    };
    const goBack = () => {
      if (currentIndex.value === 0) {
        started.value = false;
      } else {
        currentIndex.value -= 1;
      }
    };
    const finishQuiz = () => {
      finished.value = true;
      resultConfirmed.value = false;
      syncVisibleAttentionIndices();
    };
    const restartQuiz = () => {
      answers.forEach((_, index) => {
        answers[index] = null;
      });
      startQuiz();
    };
    const resetToStart = () => {
      answers.forEach((_, index) => {
        answers[index] = null;
      });
      started.value = false;
      finished.value = false;
      currentIndex.value = 0;
      resultConfirmed.value = false;
      visibleAttentionIndices.clear();
    };

    const store = useStore();
    watch(
      () => store.getters.getQuizResetSignal,
      () => {
        resetToStart();
      },
    );
    const segmentClass = (index) => {
      if (!finished.value && index === currentIndex.value) return "current";
      const answer = answers[index];
      if (answer === "no") return "answered_no";
      if (answer === "yes") return "answered_yes";
      return "not_done";
    };
    const segmentLabel = (index) => {
      const stateText = {
        current: "目前作答中",
        answered_no: "已作答：否",
        answered_yes: "已作答：是",
        not_done: "尚未作答",
      }[segmentClass(index)];
      const jumpHint = finished.value ? "" : "，可點擊跳至此題";
      return `第 ${index + 1} 題，${stateText}${jumpHint}`;
    };

    return {
      foreignAffairsGuide,
      safetyAbroadGuide,
      questions,
      riskLevels,
      started,
      finished,
      currentIndex,
      answers,
      currentQuestion,
      hasQuestionWarning,
      qrCodeDataUrl,
      qrCodeMap,
      yesCount,
      currentRiskLevel,
      activeLevelStyle,
      attentionQuestions,
      isAnswerCorrected,
      hasIncorrectAnswers,
      resultConfirmed,
      isLastQuestion,
      answeredCount,
      allAnswered,
      startQuiz,
      setAnswer,
      correctAnswer,
      confirmResult,
      goToQuestion,
      goNext,
      goBack,
      finishQuiz,
      restartQuiz,
      segmentClass,
      segmentLabel,
      showConfirmModal,
      recheckQuestion,
      skipToNext,
    };
  },
});
</script>

<style lang="scss" scoped>
.wrapper {
  text-align: left;
  margin: 20px 0 60px;
}

.header_row {
  @include flex_between();
  align-items: flex-start;
  margin-bottom: 40px;
  @include md {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .title_group {
    text-shadow: 2px 2px 5px #232323;
    .title {
      color: rgba(238, 238, 238, 1);
      margin-bottom: 8px;
      font-weight: 500;
      font-style: Medium;
      font-size: 36px;
      line-height: 45px;
      letter-spacing: 0px;
      vertical-align: middle;
    }
    p {
      color: rgba(238, 238, 238, 1);
      font-size: 18px;
    }
  }

  .download_btn {
    @include flex_center();
    flex-shrink: 0;
    background-color: $main_color;
    color: #fff;
    padding: 16px 40px;
    font-size: $h5;
    white-space: nowrap;
    svg {
      margin-left: 8px;
    }
  }
}

.diagnose_btn {
  width: 100%;
  background-color: rgba(255, 191, 71, 1);
  border: 2px dashed $black_color;
  border-radius: 4px;
  padding: 32px 40px;
  font-size: 48px;
  color: rgba(0, 0, 0, 0.85);
  cursor: pointer;
  margin-bottom: 48px;
  font-family: Inter;
  font-weight: 700;
  font-style: Bold;
  line-height: 100%;
  letter-spacing: 0px;
  display: flex;
  align-items: center;
  justify-content: center;

  .arrow {
    margin-left: 6px;
  }

  &:hover {
    opacity: 0.9;
  }
}

.quiz {
  margin-bottom: 20px;
  .quiz_progress {
    margin-bottom: 20px;
    background-color: white;
    padding: 24px;
    .quiz_progress_head {
      @include flex_between();
      margin-bottom: 8px;
      p {
        font-size: $h5;
        color: rgba(0, 0, 0, 0.85);
      }
      .quiz_progress_count {
        color: $main_color;
        font-family: "Noto_Bold";
      }
    }

    .quiz_progress_bar {
      display: flex;
      gap: 4px;
      margin-bottom: 8px;

      &.readonly .quiz_progress_seg {
        cursor: default;
      }

      .quiz_progress_seg {
        position: relative;
        flex: 1;
        height: 12px;
        cursor: pointer;
        background-color: #024d731a;

        &.current {
          background-color: rgba(2, 77, 115, 0.4);
        }
        &.answered_yes {
          background-color: $main_color;
        }
        &.answered_no {
          background-color: rgba(255, 59, 48, 1);
        }

        &:nth-child(10) {
          margin-left: 16px;

          &::before {
            content: "";
            position: absolute;
            top: 50%;
            left: -10px;
            width: 2px;
            height: 20px;
            transform: translateY(-50%);
            background-color: $main_color;
          }
        }
      }
    }

    .quiz_progress_phase {
      font-size: $p;
      color: $sidebar_gray;
    }
  }

  .quiz_card {
    background-color: white;
    border-radius: 4px;
    margin-bottom: 20px;

    .quiz_question_head {
      display: flex;
      align-items: flex-start;
      margin-bottom: 20px;
      background: var(--primary---titlebg, #024d730d);
      border-bottom: 1px solid var(--border, #e2e8f0);
      padding: 32px;
      .quiz_number {
        @include flex_center();
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        background-color: $main_color;
        color: #fff;
        font-family: "Noto_Bold";
        margin-right: 14px;
      }

      .quiz_question_text {
        .quiz_question {
          font-size: 25px;
          color: #000000d9;
          margin-bottom: 6px;
          font-weight: 500;
        }
        .quiz_desc {
          font-size: $h5;
          color: #64748b;
        }
      }

      .corrected_badge {
        @include flex_center();
        flex-shrink: 0;
        margin-left: auto;
        background-color: #2e7d321a;
        color: #2e7d32;
        border-radius: 4px;
        padding: 4px 10px;
        font-size: $p;
        font-family: "Noto_Bold";
        white-space: nowrap;
        :deep(svg) {
          margin-right: 4px;
        }
      }
    }

    .quiz_answers {
      display: flex;
      gap: 20px;
      @include sm {
        flex-direction: column;
      }

      .quiz_answer_btn {
        @include flex_center();
        flex: 1;
        background-color: #fff;
        border: 2px solid var(--border, #e2e8f0);
        padding: 24px;
        font-size: 20px;
        font-weight: 700;
        color: #000000d9;
        cursor: pointer;

        .answer_icon {
          display: inline-block;
          width: 25px;
          height: 25px;
          margin-right: 8px;
          background-color: #c2ccd6;
          -webkit-mask-position: center;
          mask-position: center;
          -webkit-mask-size: contain;
          mask-size: contain;
          -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;

          &.answer_icon_yes {
            -webkit-mask-image: url("../img/answer_yes.png");
            mask-image: url("../img/answer_yes.png");
          }

          &.answer_icon_no {
            -webkit-mask-image: url("../img/answer_no.png");
            mask-image: url("../img/answer_no.png");
          }
        }

        &.selected {
          background-color: rgba($main_color, 0.1);
          border: 2px solid $main_color;
          color: $main_color;
          font-family: "Noto_Bold";

          .answer_icon {
            background-color: $main_color;
          }
        }

        &.readonly {
          cursor: default;
          pointer-events: none;
        }

        &.no_selected {
          background-color: rgba(#ff3b30, 0.1);
          border: 2px solid #ff3b30;
          color: #ff3b30;
          font-family: "Noto_Bold";

          .answer_icon {
            background-color: #ff3b30;
          }
        }
      }
    }

    .quiz_warning {
      @include flex_between();
      align-items: center;
      background-color: #fff;
      border: 1px solid var(--secondary, #ffbf47);
      background: var(--secondary---bg, #f8f6f3);
      padding: 20px;
      @include sm {
        flex-direction: column;
        gap: 12px;
      }

      .quiz_warning_body {
        @include flex();
        align-items: start;
        gap: 12px;
      }

      .quiz_warning_icon {
        flex-shrink: 0;
        width: 15px;
        height: 20px;
      }

      .quiz_warning_text {
        p {
          @include flex();
          align-items: center;
          font-size: $h5;
          color: #92400e;
          margin-bottom: 8px;
          .warning_icon {
            flex-shrink: 0;
            color: $main_sec_color;
            margin-right: 4px;
          }
        }
        a {
          @include flex();
          align-items: center;
          font-size: $p;
          color: $main_color;
          :deep(svg) {
            flex-shrink: 0;
            margin-right: 4px;
          }
        }
      }

      .quiz_warning_qr {
        @include flex_center();
        flex-shrink: 0;
        width: 70px;
        height: 70px;
        margin-left: 20px;
        border: 1px solid $hr_gray;
        border-radius: 4px;
        color: $sidebar_gray;
        font-size: 0.75em;
        text-align: center;
        overflow: hidden;

        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        @include sm {
          margin-left: 0;
        }
      }
    }
  }

  .quiz_nav {
    @include flex_between();

    .quiz_back_btn,
    .quiz_next_btn {
      @include flex_center();
      padding: 10px 24px;
      font-size: $h5;
      cursor: pointer;
    }

    .quiz_back_btn {
      background-color: #fff;
      border: 1px solid $hr_gray;
      color: $black_color;
      :deep(svg) {
        margin-right: 6px;
      }
    }

    .quiz_next_btn {
      background-color: $main_color;
      border: none;
      color: #fff;
      margin-left: auto;
      :deep(svg) {
        margin-left: 6px;
      }
      &:hover {
        opacity: 0.85;
      }
      &[disabled] {
        background-color: $main_sec_color;
        color: $black_color;
        cursor: default;
        &:hover {
          opacity: 1;
        }
      }

      &.restart_btn {
        background-color: rgba(255, 191, 71, 1);
        color: rgba(0, 0, 0, 0.85);
      }
    }
  }

  .quiz_result {
    .result_box {
      background-color: white;
      border: 1px solid var(--border, #e2e8f0);
      border-radius: 4px;
      padding: 24px;
      margin-bottom: 20px;
    }

    .risk_reference {
      margin-bottom: 32px;

      .risk_reference_title {
        @include flex();
        align-items: center;
        margin-bottom: 16px;
        :deep(svg) {
          color: $main_color;
          margin-right: 8px;
        }
        h3 {
          color: #000000d9;
          font-family: "Noto Sans TC";
          font-weight: 700;
          font-size: 1.25em;
        }
      }

      .risk_reference_list {
        display: flex;
        gap: 24px;
        @include sm {
          flex-direction: column;
        }

        .risk_reference_item {
          flex: 1;
          padding: 16px;

          &.active {
            background: #007aff1a;
            border: 1px solid #2e7d321a;
          }

          .risk_name {
            @include flex();
            align-items: center;
            font-family: "Noto_Bold";
            color: $black_color;
            margin-bottom: 6px;

            .risk_dot {
              flex-shrink: 0;
              width: 6px;
              height: 12px;
              border-radius: 3px;
              margin-right: 8px;
            }
          }

          .risk_desc {
            font-size: $p;
            color: $sidebar_gray;
          }
        }
      }
    }

    .result_summary {
      @include flex_center();
      justify-content: center;
      text-align: left;
      margin-bottom: 20px;

      .result_icon_bg {
        @include flex_center();
        flex-shrink: 0;
        width: 56px;
        height: 56px;
        border-radius: 50%;
        margin-right: 16px;
      }

      .result_icon {
        @include flex_center();
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        color: #fff;
        font-size: 0.85em;
      }

      .result_text_group {
        h3 {
          font-family: "Noto Sans TC";
          font-weight: bold;
          font-size: 36px;
          margin-bottom: 4px;
        }

        .result_subtitle {
          color: #64748b;
          font-size: $h5;
        }
      }
    }

    .result_line {
      text-align: start;
      color: $black_color;
      font-family: "Noto_Bold";
      font-size: $h4;
      margin-bottom: 12px;
    }

    .result_advice {
      text-align: start;
      color: $sidebar_gray;
      font-size: $h5;
      margin-bottom: 32px;
    }

    .attention_title {
      color: $black_color;
      font-family: "Noto_Bold";
      font-size: $h4;
      margin-bottom: 16px;
    }

    .attention_card {
      border: 1px solid var(--border, #e2e8f0);
      margin-bottom: 20px;

      &.corrected {
        border-color: #2e7d32;
      }
    }

    .other_reminder {
      background-color: #fff;
      border: 1px solid var(--border, #e2e8f0);
      border-radius: 4px;
      padding: 20px;
      margin-top: 20px;

      .other_reminder_head {
        @include flex();
        align-items: center;
        margin-bottom: 8px;
        :deep(svg) {
          width: 24px;
          height: 24px;
          color: $main_color;
          margin-right: 8px;
        }
        p {
          color: #000000d9;
          font-family: "Noto Sans TC";
          font-size: 20px;
        }
      }

      .other_reminder_body {
        @include flex_between();
        align-items: center;
        gap: 20px;
        @include sm {
          flex-direction: column;
          align-items: flex-start;
        }

        .other_reminder_text {
          color: #000000d9;
          font-size: $h5;
          font-family: "Noto Sans TC";
          .highlight {
            color: $black_color;
            font-weight: bold;
          }
        }

        .other_reminder_download {
          @include flex_center();
          flex-shrink: 0;
          background-color: $main_color;
          color: #fff;
          padding: 10px 24px;
          font-size: $h5;
          white-space: nowrap;
          :deep(svg) {
            margin-left: 6px;
          }
          &:hover {
            opacity: 0.85;
          }
        }
      }
    }
  }
}

:deep(.confirm_modal) {
  .ant-modal-body {
    padding: 32px;
  }
}

.confirm_modal_body {
  text-align: center;

  .confirm_icon {
    @include flex_center();
    margin: 0 auto 16px;

    img {
      width: 56px;
      height: 56px;
    }
  }

  h3 {
    font-family: "Noto Sans TC";
    font-weight: 500;
    color: #000000d9;
    margin-bottom: 12px;
  }

  p {
    font-size: $h5;
    color: #475569;
    margin-bottom: 32px;
  }

  .confirm_recheck_btn,
  .confirm_skip_btn {
    @include flex_center();
    width: 100%;
    padding: 12px;
    font-size: $h5;
    font-weight: bold;
    cursor: pointer;
    :deep(svg) {
      margin-left: 6px;
    }
  }

  .confirm_recheck_btn {
    background-color: $main_color;
    border: none;
    color: #fff;
    margin-bottom: 12px;
    &:hover {
      opacity: 0.85;
    }
  }

  .confirm_skip_btn {
    background-color: #fff;
    border: 1px solid $hr_gray;
    color: $black_color;
    &:hover {
      opacity: 0.85;
    }
  }
}

.hotline_card {
  @include flex_between();
  background-color: #fff;
  border: 1px solid var(--border, rgba(226, 232, 240, 1));
  padding: 20px 30px;
  margin-bottom: 84px;
  @include md {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .hotline_item {
    .label {
      font-size: 16px;
      font-family: "Noto Sans TC";
      color: #000000d9;
    }
    .number {
      font-size: 24px;
      font-weight: 900;
      color: #024d73;
    }
  }

  .usage_link {
    @include flex_center();
    color: rgba(2, 77, 115, 1);
    font-size: 16px;
    :deep(svg) {
      margin-left: 6px;
      width: 24px;
      height: 24px;
    }
  }
}

.gov_resource {
  text-align: center;
  margin-bottom: 30px;

  .gov_resource_title {
    @include flex_center();
    margin-bottom: 8px;
    :deep(svg) {
      color: $main_color;
      width: 24px;
      height: 24px;
      margin-right: 8px;
    }
    p {
      color: rgba(0, 0, 0, 0.85);
      font-size: 24px;
    }
  }

  .gov_resource_desc {
    color: rgba(100, 116, 139, 1);
    font-size: 16px;
    margin-bottom: 24px;
  }

  .gov_resource_list {
    display: flex;
    flex-wrap: nowrap;
    justify-content: center;
    gap: 20px;
    overflow-x: auto;
    @include sm {
      gap: 10px;
    }

    .gov_resource_item {
      @include flex_center();
      flex: 1 1 0;
      min-width: 0;
      background-color: #fff;
      border-radius: 4px;
      border: 3px dashed var(--border, rgba(226, 232, 240, 1));
      padding: 24px;
      @include sm {
        padding: 14px 8px;
      }

      .icon_circle {
        @include flex_center();
        width: 48px;
        height: 48px;
        border-radius: 50%;
        margin-right: 12px;
        overflow: hidden;
        flex-shrink: 0;
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        svg {
          color: $main_color;
          font-size: 1.25em;
        }
      }

      .text_group {
        text-align: left;
        min-width: 0;

        p {
          color: rgba(0, 0, 0, 0.85);
          font-size: 16px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          @include sm {
            font-size: $p;
          }
        }

        .desc {
          margin-top: 2px;
        }
      }

      &:hover {
        .text_group .desc {
          color: $main_color;
          text-decoration: underline;
        }
      }
    }
  }
}
</style>
